import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import {
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import api from '../utils/api';

/** Distinct card palettes — stable by repository name hash. */
const CARD_PALETTES = [
  { bg: 'linear-gradient(145deg, #ecfdf5 0%, #d1fae5 55%, #a7f3d0 100%)', border: '#059669', accent: '#047857', chip: '#065f46' },
  { bg: 'linear-gradient(145deg, #eff6ff 0%, #dbeafe 55%, #bfdbfe 100%)', border: '#2563eb', accent: '#1d4ed8', chip: '#1e40af' },
  { bg: 'linear-gradient(145deg, #fff7ed 0%, #ffedd5 55%, #fed7aa 100%)', border: '#ea580c', accent: '#c2410c', chip: '#9a3412' },
  { bg: 'linear-gradient(145deg, #f0fdfa 0%, #ccfbf1 55%, #99f6e4 100%)', border: '#0d9488', accent: '#0f766e', chip: '#115e59' },
  { bg: 'linear-gradient(145deg, #fef2f2 0%, #fecaca 55%, #fca5a5 100%)', border: '#dc2626', accent: '#b91c1c', chip: '#991b1b' },
  { bg: 'linear-gradient(145deg, #fefce8 0%, #fef08a 55%, #fde047 100%)', border: '#ca8a04', accent: '#a16207', chip: '#854d0e' },
  { bg: 'linear-gradient(145deg, #f8fafc 0%, #e2e8f0 55%, #cbd5e1 100%)', border: '#475569', accent: '#334155', chip: '#1e293b' },
  { bg: 'linear-gradient(145deg, #fdf4ff 0%, #f5d0fe 55%, #e879f9 100%)', border: '#c026d3', accent: '#a21caf', chip: '#86198f' },
  { bg: 'linear-gradient(145deg, #fff1f2 0%, #fecdd3 55%, #fda4af 100%)', border: '#e11d48', accent: '#be123c', chip: '#9f1239' },
  { bg: 'linear-gradient(145deg, #ecfeff 0%, #a5f3fc 55%, #67e8f9 100%)', border: '#0891b2', accent: '#0e7490', chip: '#155e75' }
];

const paletteFor = (repository) => {
  let hash = 0;
  const s = String(repository || '');
  for (let i = 0; i < s.length; i += 1) {
    hash = ((hash << 5) - hash) + s.charCodeAt(i);
    hash |= 0;
  }
  return CARD_PALETTES[Math.abs(hash) % CARD_PALETTES.length];
};

const shortLabel = (repository) => {
  const parts = String(repository || '').split(/[-_/]/).filter(Boolean);
  if (parts.length <= 2) return repository;
  // e.g. tenanthub-superadmin-frontend → frontend
  return parts.slice(-2).join('-');
};

/**
 * Loads image repositories + tags from ACR (via Super Admin API)
 * and lets the operator opt-in per repository and pick a tag.
 */
const AcrImageVersionPicker = ({ value = [], onChange, shortNames = [] }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registry, setRegistry] = useState('');
  const [images, setImages] = useState([]);
  const [selections, setSelections] = useState({});
  const [enabled, setEnabled] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [cacheNote, setCacheNote] = useState('');

  const applyDefaults = useCallback((repos, existingValue) => {
    const byRepo = new Map((existingValue || []).map((row) => [row.repository, row.tag]));
    const nextSelections = {};
    const nextEnabled = {};

    repos.forEach((repo) => {
      const tags = repo.tags || [];
      const existingTag = byRepo.get(repo.repository);
      if (existingTag && tags.includes(existingTag)) {
        nextSelections[repo.repository] = existingTag;
      } else if (tags.includes('latest')) {
        nextSelections[repo.repository] = 'latest';
      } else {
        nextSelections[repo.repository] = tags[0] || '';
      }
      // Default unchecked — user opts in. When editing, restore previously saved images.
      nextEnabled[repo.repository] = byRepo.has(repo.repository);
    });

    return { nextSelections, nextEnabled };
  }, []);

  const emitChange = useCallback((repos, nextSelections, nextEnabled) => {
    if (!onChange) return;
    const payload = repos
      .filter((repo) => nextEnabled[repo.repository] && nextSelections[repo.repository])
      .map((repo) => ({
        repository: repo.repository,
        tag: nextSelections[repo.repository],
        image: `${repo.image}:${nextSelections[repo.repository]}`,
        catalog_short_name: repo.catalog_link?.short_name || null,
        catalog_kind: repo.catalog_link?.kind || null
      }));
    onChange(payload);
  }, [onChange]);

  const shortNamesKey = useMemo(
    () => (shortNames || []).map((s) => String(s).toLowerCase()).filter(Boolean).sort().join(','),
    [shortNames]
  );

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = { offered: true };
      if (opts.refresh) params.refresh = true;
      if (shortNamesKey) params.short_names = shortNamesKey;
      const response = await api.get('/catalog/acr/images', { params });
      const data = response.data.data || {};
      const repos = data.images || [];
      setRegistry(data.registry || 'pilvisystems.azurecr.io');
      setImages(repos);
      const { nextSelections, nextEnabled } = applyDefaults(repos, value);
      setSelections(nextSelections);
      setEnabled(nextEnabled);
      emitChange(repos, nextSelections, nextEnabled);
      if (data.cache?.from_cache) {
        setCacheNote(
          data.cache.unchanged
            ? `Using cached registry catalog${data.cache.pulled_at ? ` (last pull ${new Date(data.cache.pulled_at).toLocaleString()})` : ''}. Refresh only pulls when tags change.`
            : 'Loaded from registry cache.'
        );
      } else {
        setCacheNote(data.cache?.pulled_at ? `Registry catalog refreshed at ${new Date(data.cache.pulled_at).toLocaleString()}.` : '');
      }
      if (repos.length === 0) {
        setError(
          shortNamesKey
            ? 'No offered images match the selected Software Applications / Services. Link repositories in Settings → Container Images (ACR), or offer tags for those shortnames.'
            : 'No container images are offered for tenants. An administrator must enable image tags in Settings → Container Images (ACR).'
        );
      }
    } catch (err) {
      setImages([]);
      setError(err.response?.data?.message || 'Failed to load ACR image versions');
    } finally {
      setLoading(false);
    }
  }, [applyDefaults, emitChange, shortNamesKey, value]);

  useEffect(() => {
    load();
    // reload when selected catalog shortnames change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortNamesKey]);

  const onToggleEnabled = (repository, checked) => {
    const nextEnabled = { ...enabled, [repository]: checked };
    let nextSelections = selections;
    if (checked && !selections[repository]) {
      const repo = images.find((r) => r.repository === repository);
      const tags = repo?.tags || [];
      const fallback = tags.includes('latest') ? 'latest' : (tags[0] || '');
      nextSelections = { ...selections, [repository]: fallback };
      setSelections(nextSelections);
    }
    setEnabled(nextEnabled);
    emitChange(images, nextSelections, nextEnabled);
  };

  const onSelectTag = (repository, tag) => {
    const next = { ...selections, [repository]: tag };
    setSelections(next);
    emitChange(images, next, enabled);
  };

  const enabledCount = Object.values(enabled).filter(Boolean).length;

  const applySearch = () => {
    setSearchQuery(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const filteredImages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return images;
    return images.filter((repo) => {
      const haystack = [
        repo.repository,
        repo.image,
        shortLabel(repo.repository),
        ...(repo.tags || [])
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [images, searchQuery]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h6">Container images (ACR)</Typography>
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={() => load({ refresh: true })}
          disabled={loading}
        >
          Check for new images
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Only image tags enabled in <strong>Settings → Container Images (ACR)</strong> are listed
        (latest six are offered by default when an admin opens a repository).
        Versions from <strong>{registry || 'pilvisystems.azurecr.io'}</strong>.
        Check a card to include that image and choose its tag.
        {images.length > 0 && (
          <> · <strong>{enabledCount}</strong> of {images.length} selected</>
        )}
      </Typography>
      {cacheNote ? (
        <Alert severity="info" sx={{ mb: 2 }}>{cacheNote}</Alert>
      ) : null}

      {images.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mb: 2,
            flexWrap: 'wrap',
            alignItems: 'stretch'
          }}
        >
          <TextField
            size="small"
            placeholder="Search repositories, images, or tags…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applySearch();
              }
            }}
            sx={{ flex: '1 1 240px', minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={clearSearch} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={applySearch}
            sx={{ px: 2.5, whiteSpace: 'nowrap' }}
          >
            Search
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !images.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}

      {!loading && !error && images.length === 0 && (
        <Alert severity="info">No repositories found in ACR for the configured filter.</Alert>
      )}

      {!loading && images.length > 0 && filteredImages.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No images match “{searchQuery}”. Clear search or try another term.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))'
          },
          gap: 2
        }}
      >
        {filteredImages.map((repo) => {
          const palette = paletteFor(repo.repository);
          const isOn = Boolean(enabled[repo.repository]);
          const hasTags = Boolean(repo.tags?.length);
          const missingTag = isOn && !selections[repo.repository];

          return (
            <Box
              key={repo.repository}
              sx={{
                position: 'relative',
                borderRadius: 2.5,
                border: '2px solid',
                borderColor: isOn ? palette.border : 'transparent',
                background: isOn ? palette.bg : 'linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%)',
                boxShadow: isOn
                  ? `0 8px 24px -12px ${palette.border}99, inset 0 1px 0 rgba(255,255,255,0.65)`
                  : 'inset 0 0 0 1px rgba(15,23,42,0.08)',
                opacity: isOn ? 1 : 0.72,
                transition: 'border-color 160ms ease, box-shadow 160ms ease, opacity 160ms ease, transform 160ms ease',
                transform: isOn ? 'translateY(0)' : 'none',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 200,
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: isOn
                    ? `linear-gradient(90deg, ${palette.border}, ${palette.accent})`
                    : '#94a3b8'
                }
              }}
            >
              <FormControlLabel
                sx={{
                  m: 0,
                  mb: 1,
                  alignItems: 'flex-start',
                  '.MuiFormControlLabel-label': { width: '100%' }
                }}
                control={
                  <Checkbox
                    checked={isOn}
                    onChange={(e) => onToggleEnabled(repo.repository, e.target.checked)}
                    disabled={!hasTags && !repo.error}
                    sx={{
                      color: palette.accent,
                      '&.Mui-checked': { color: palette.accent },
                      pt: 0.25
                    }}
                  />
                }
                label={(
                  <Box>
                    <Typography
                      fontWeight={800}
                      sx={{
                        color: isOn ? palette.chip : '#64748b',
                        lineHeight: 1.25,
                        wordBreak: 'break-word'
                      }}
                    >
                      {shortLabel(repo.repository)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: isOn ? palette.accent : '#94a3b8',
                        display: 'block',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                        fontSize: '0.7rem',
                        wordBreak: 'break-all'
                      }}
                    >
                      {repo.repository}
                    </Typography>
                  </Box>
                )}
              />

              <Typography
                variant="caption"
                sx={{
                  color: isOn ? palette.accent : '#94a3b8',
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {repo.error
                  ? repo.error
                  : `${(repo.tags || []).length} tag(s) · ${repo.image}`}
              </Typography>

              <Box sx={{ mt: 'auto' }}>
                <FormControl fullWidth size="small" disabled={!isOn || !hasTags} required={isOn}>
                  <InputLabel>Image version</InputLabel>
                  <Select
                    label="Image version"
                    value={isOn ? (selections[repo.repository] || '') : ''}
                    onChange={(e) => onSelectTag(repo.repository, e.target.value)}
                    error={missingTag}
                    sx={{
                      bgcolor: isOn ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.35)',
                      borderRadius: 1.5,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: missingTag ? '#dc2626' : `${palette.border}66`
                      }
                    }}
                  >
                    {(repo.tags || []).map((tag) => (
                      <MenuItem key={tag} value={tag}>
                        {tag}
                        {tag === 'latest' ? ' (latest)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {missingTag && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                    Choose a version for this image
                  </Typography>
                )}
                {!isOn && (
                  <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: '#94a3b8' }}>
                    Unchecked — not included for this tenant
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AcrImageVersionPicker;
